<template>
  <section class="mt-2 border-t border-gray-200 pt-8">
    <div class="flex items-center justify-between gap-4 mb-4">
      <h2 class="text-2xl font-bold text-gray-900">Avaliações</h2>
      <div v-if="loading" class="text-sm text-gray-600">
        Carregando...
      </div>
    </div>

    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
      {{ error }}
    </div>

    <div v-else>
      <div v-if="loading" class="text-gray-600">
        Carregando avaliações...
      </div>

      <div v-else-if="evaluations.length === 0" class="text-gray-600">
        Ainda não há avaliações para este imóvel.
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(ev, idx) in evaluations"
          :key="'guest-ev-' + (ev && ev.id != null ? ev.id : 'n') + '-' + idx"
          class="bg-white border border-gray-200 rounded-lg p-4"
        >
          <div class="flex items-start gap-3 flex-wrap">
            <div class="flex text-lg leading-none">
              <span
                v-for="n in 5"
                :key="n"
                :class="n <= Number(ev.guest_stars || 0) ? 'text-amber-500' : 'text-gray-300'"
              >
                ★
              </span>
            </div>

            <div class="text-sm text-gray-900 font-semibold">
              {{ ev.guest_stars }}/5
              <span v-if="ev.guest_name">
                ({{ ev.guest_name }} - {{ formatDateTime(ev.guest_evaluated_at) }})
              </span>
              <span v-else>
                ({{ formatDateTime(ev.guest_evaluated_at) }})
              </span>
            </div>
          </div>

          <div v-if="ev.guest_comment" class="mt-2 text-gray-700 whitespace-pre-wrap">
            {{ ev.guest_comment }}
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="mt-4 flex justify-center">
        <button
          type="button"
          @click="loadMore"
          :disabled="loadingMore"
          class="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
        >
          {{ loadingMore ? 'Carregando...' : 'Ver mais' }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PropertyGuestEvaluations',
  props: {
    propertyId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      evaluations: [],
      loading: false,
      loadingMore: false,
      error: null,
      limit: 10,
      offset: 0,
      hasMore: false,
      fetchAbort: null
    }
  },
  watch: {
    propertyId: {
      immediate: true,
      handler() {
        this.resetAndLoad()
      }
    }
  },
  beforeUnmount() {
    if (this.fetchAbort) {
      this.fetchAbort.abort()
      this.fetchAbort = null
    }
  },
  methods: {
    async resetAndLoad() {
      if (!this.propertyId) return

      if (this.fetchAbort) {
        this.fetchAbort.abort()
        this.fetchAbort = null
      }

      this.evaluations = []
      this.offset = 0
      this.hasMore = false
      await this.fetchEvaluations({ replace: true })
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore) return
      await this.fetchEvaluations({ replace: false })
    },

    async fetchEvaluations({ replace }) {
      if (!this.propertyId) return

      if (replace) this.loading = true
      else this.loadingMore = true

      const requestedPropertyId = this.propertyId
      const requestedOffset = this.offset

      const ac = new AbortController()
      if (replace) {
        if (this.fetchAbort) this.fetchAbort.abort()
        this.fetchAbort = ac
      }

      try {
        this.error = null

        const url = `/api/v1/properties/public/${encodeURIComponent(requestedPropertyId)}/guest_evaluations?limit=${this.limit}&offset=${requestedOffset}&_=${Date.now()}`
        const response = await fetch(url, {
          signal: ac.signal,
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        })
        if (!response.ok) throw new Error('Erro ao carregar avaliações')

        const payload = await response.json()
        let rows = this.normalizeEvaluationRows(payload?.data)

        // Resposta obsoleta (trocou de imóvel ou já houve outro reset / "Ver mais").
        if (String(this.propertyId) !== String(requestedPropertyId)) return
        if (!replace && this.offset !== requestedOffset) return

        if (replace) {
          this.evaluations = [...rows]
        } else {
          const seen = new Set(this.evaluations.map((e) => e.id))
          const merged = [...this.evaluations]
          for (const row of rows) {
            if (row.id != null && !seen.has(row.id)) {
              seen.add(row.id)
              merged.push(row)
            }
          }
          this.evaluations = merged
        }

        this.hasMore = !!payload.has_more && rows.length === this.limit
        this.offset += rows.length
      } catch (e) {
        if (e?.name === 'AbortError') return
        if (String(this.propertyId) === String(requestedPropertyId)) {
          this.error = e?.message || 'Erro ao carregar avaliações'
        }
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    normalizeEvaluationRows(data) {
      if (Array.isArray(data)) return data
      if (data && typeof data === 'object') {
        return Object.values(data).filter(
          (x) => x && typeof x === 'object' && ('guest_evaluated_at' in x || 'guest_stars' in x)
        )
      }
      return []
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return ''
      const d = new Date(dateTimeString)
      if (Number.isNaN(d.getTime())) return ''
      const date = d.toLocaleDateString('pt-BR')
      const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      return `${date} ${time}`
    }
  }
}
</script>

